import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { FormInstance } from 'antd/es/form';
import { RootState } from 'modules';
import { useRecoilState, useRecoilValue } from 'recoil';
import { getUrlQuery } from 'libs/utils';
import { TQuizOption, TQuizAnswer, TCurrentProcess, EQuizProcessName } from 'modules/learning';
import { addOption } from 'modules/learning/createQuiz';
import { currentProcessState, gradingLoadingState, isGradedState } from 'modules/learning/recoilState';
import { Form, Tabs, Button, Input, InputNumber, Spin } from 'antd';
import styled from 'styled-components';
import {
  PrimaryColor,
  Colors,
  PlusOutlineIcon,
  MinusOutlineIcon,
  StyledSectionCover,
  StyledCheckIcon,
  device,
  StyledIconBackground,
} from 'GlobalStyles';

const { TabPane } = Tabs;

type TQuizChoiceOptionProps = {
  form: FormInstance;
  options: Array<TQuizOption>;
  setOptions: (x: any) => void;
  answersExceptDefault: Array<TQuizAnswer>;
  setAnswersExceptDefault: (x: any) => void;
  userAnswer: Array<TQuizOption>;
  setUserAnswer: (x: any) => void;
};

function KaitusAssessmentChoiceOption({
  form,
  options,
  setOptions,
  answersExceptDefault,
  setAnswersExceptDefault,
  userAnswer,
  setUserAnswer,
}: TQuizChoiceOptionProps) {
  const location = useLocation();
  const { operation, id } = getUrlQuery(location.search);
  const [addedOptionList, setAddedOptionList] = useState<Array<TQuizOption>>([]);
  const dispatch = useDispatch();
  const quizState = useSelector((store: RootState) => store.createQuizReducer);
  const currentProcess = useRecoilValue<TCurrentProcess>(currentProcessState);
  const gradingLoading = useRecoilValue<boolean>(gradingLoadingState);
  const isGraded = useRecoilValue<boolean>(isGradedState);
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);

  const handleClickOption = (clickedOption: TQuizOption) => {
    if (
      //리스트 타입일 때
      quizState.answer_type === 'list' && //리스트일 때만 클릭할 수 있음 ==> 목차유형에서는 클릭할 때는 변동 없도록
      userAnswer.length < answersExceptDefault.length //User가 선택한 수가 진짜 답변 개수보다 작을 때 더 선택할 수 있도록
    ) {
      const filterClickedOption = options.filter((el: TQuizOption) => {
        return el.id !== clickedOption.id;
      });

      setOptions(filterClickedOption);
      setUserAnswer([...userAnswer, clickedOption]);
    }
  };

  const onClickAddOption = () => {
    const optionContent = form.getFieldValue('add_option');
    const optionNumber = form.getFieldValue('add_option_number');
    setAddedOptionList([...addedOptionList, { content_number: optionNumber, content: optionContent }]);
    form.setFieldsValue({
      add_option: undefined,
      add_option_number: undefined,
    });
  };

  const onClickDeleteOption = (number: number, content: string) => {
    const filteredOptionList = addedOptionList.filter((el: TQuizOption) => {
      return el.content_number !== number && el.content !== content;
    });
    setAddedOptionList(filteredOptionList);
  };

  useEffect(() => {
    dispatch(addOption(addedOptionList));
  }, [addedOptionList]);

  useEffect(() => {
    if (id) {
      //어드민 수정의 경우 원래 추가 했었던 리스트 세팅 ==> addedOptionList가 추가 삭제될 때마다 리덕스 업데이트
      setAddedOptionList(quizState.quiz_option);
    }
  }, [quizState.id]); //==> 수정 데이터 들어올 때 업데이트 되는 데이터 받을 수 있도록

  return (
    <>
      {operation === 'view' && (
        <StyledQuizChoiceOptionWrapper
          quizType={quizState.answer_type}
          id={`${EQuizProcessName.Option}_popper_reference`}
          currentProcess={currentProcess.name === EQuizProcessName.Option}
        >
          <Tabs>
            <TabPane
              tab={'선택지'}
              // key={ERoute.MentoringQNA}
            ></TabPane>
          </Tabs>
          <Spin
            tip='제출중..'
            spinning={(currentUser?.is_drill_membership || quizState.is_sample) && gradingLoading}
          >
            <StyledSectionWrapper>
              {isGraded && <StyledSectionCover />}
              <StyledQuizSelectOptions isGraded={isGraded}>
                {options.map((el: TQuizOption) => {
                  return (
                    <Button
                      className={`outline ${quizState.quiz_answer
                        .map((answer: any) => answer.id)}`}
                      onClick={() => handleClickOption(el)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div>{`${el.content_number} . ${el.content}`}</div>
                        { /*!gradingLoading &&
                          isGraded &&
                          quizState.quiz_answer.map((answer: any) => answer.id).includes(el.id) && (
                            <StyledIconBackground type='green' position='relative'>
                              <StyledCheckIcon />
                            </StyledIconBackground>
                          )*/ }
                      </div>
                    </Button>
                  );
                })}
              </StyledQuizSelectOptions>
            </StyledSectionWrapper>
          </Spin>
        </StyledQuizChoiceOptionWrapper>
      )}
      {operation !== 'view' && (
        <StyledMakeQuizOptionWrapper>
          <Tabs>
            <TabPane
              tab={'선택지'}
              // key={ERoute.MentoringQNA}
            ></TabPane>
          </Tabs>
          <StyledQuizSelectOptions style={{ marginTop: '20px', marginLeft: '10px' }}>
            {/* 선택지 작성 */}
            <StyledInputWrapper>
              <Form.Item name={'add_option_number'} style={{ width: '10%' }}>
                <InputNumber className='option_input' placeholder='번호' />
              </Form.Item>
              <Form.Item name={'add_option'} style={{ width: '75%' }}>
                <Input className='option_input' placeholder='선택지 입력창' />
              </Form.Item>
              <Button className='list-plus-button' onClick={onClickAddOption}>
                <PlusOutlineIcon fill={PrimaryColor} />
              </Button>
            </StyledInputWrapper>
            {/* 작성한 선택지 리스트 */}
            <StyledAddedOptionWrapper>
              {addedOptionList.length > 0 &&
                addedOptionList.map((el: TQuizOption) => {
                  return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Button
                        className='list-minus-button'
                        onClick={() => onClickDeleteOption(el.content_number, el.content)}
                      >
                        <MinusOutlineIcon fill={PrimaryColor} />
                      </Button>
                      <Button className='btn-disabled'>{`${el.content_number}. ${el.content}`}</Button>
                    </div>
                  );
                })}
            </StyledAddedOptionWrapper>
          </StyledQuizSelectOptions>
        </StyledMakeQuizOptionWrapper>
      )}
    </>
  );
}

export default KaitusAssessmentChoiceOption;

type TStyledWrapperProps = {
  quizType?: string;
  currentProcess: boolean;
};

type TSolveAgianProps = {
  isGraded?: boolean;
};

//사용자 뷰
const StyledQuizChoiceOptionWrapper = styled.div<TStyledWrapperProps>`
  //💜 도움말에서 해당 파트일 때
  ${(props) => props.currentProcess && 'z-index: 90;'}
  @media screen and ${device.sm} {
    ${(props) => props.currentProcess && 'z-index: unset;'}
  }
  position: relative;
  background-color: ${Colors.white};

  border: 1px solid ${Colors.grey1};
  height: 400px;
  & .ant-btn {
    height: 44px;
    text-align: left;
    display: block;
    margin: 10px 10px 10px 20px;
    padding: 10px 15px;
  }
  & .ant-btn:nth-child(1) {
    margin: 20px 10px 10px 20px;
  }

  & .ant-spin-nested-loading > div > .ant-spin {
    background-color: #e4e4e4;
    opacity: 0.5;
  }
`;

const StyledSectionWrapper = styled.div`
  position: relative;
  width: inherit;
  height: 355px;
  overflow: scroll;
  & .ant-btn {
    padding: 0 11px !important;
  }
`;

const StyledQuizSelectOptions = styled.section<TSolveAgianProps>`
  ${(props) => props.isGraded && 'pointer-events : none'}
`;

//문제 생성
const StyledMakeQuizOptionWrapper = styled.section`
  border: 1px solid ${Colors.grey1};
  height: 400px;
`;
//문제 생성 - 작성 input부분
export const StyledInputWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  & .ant-form-item {
    margin-bottom: 0;
  }
  & .option_input {
    border-radius: 8px !important;
    border: 1px solid ${PrimaryColor};
  }
`;
//문제 생성 - 입력한 문제들
export const StyledAddedOptionWrapper = styled.div`
  margin-top: 10px;
`;
